import {Step} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/step';
import {TestPassState, TestQuestion, TestResponse, TestResponseCommands} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/models';
import {CheckItemsManager} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/check-items-manager';
import {Observable, of} from 'rxjs';
import {Role} from '@app/modules/shared/data/constants';
import {delay} from 'rxjs/operators';

export class AudioCheckManager extends CheckItemsManager {
  icon = 'sound-g';
  name = 'Sound';
  audio = new Audio();

  constructor(userRole: Role) {
    super(userRole);
    this.steps.push(new HeadsetPluggedIn(), new AudioPlayback(), new AudioSource());
  }

  override testPostProcessing() {
    if (this.steps[0].testResult.status === TestPassState.FAILED || this.steps[1].testResult.status === TestPassState.FAILED || !this.steps[0].testQuestion.answerOptions[2].isDisabled) {
      this.steps[2].isHidden = true;
    }
  }
}

export class HeadsetPluggedIn extends Step {
  name = 'Headset plugged in';
  title = 'Checking headset';

  constructor() {
    super(true);
    this.testQuestion = new TestQuestion('Please check if your headset is plugged in', [
        {text: 'I don\'t have one', value: 'no', color: 'blue', isBlock: false, isDisabled: false},
        {text: 'Yes, it is connected', value: 'yes', color: 'blue', isBlock: false, isDisabled: false},
        {text: 'I am logging in for evaluation', value: 'yes', color: 'blue', isBlock: false, isDisabled: true},
      ]);
  }

  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        this.testResult.errorMassage = 'Using a headset is absolutely crucial.' +
          ' If you have a headset, please connect it, wait until it is ' +
          'recognized by driver and retry this step. You may need to refresh ' +
          'this page and start diagnostics over again. If you do not have a headset' +
          ' at all, skip this step for now. You will need to get one for future classes.';
        this.testResult.text = 'no headset';
        this.testStatus = 'NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        if(!this.testQuestion.answerOptions[2].isDisabled){
          this.testResult.status = TestPassState.PASSED;
          this.testResult.text = 'not required';
          this.testStatus = 'OK';
          subscriber.next(new TestResponse(TestResponseCommands.NEXT));
        }else {
          this.testResult.status = TestPassState.PASSED;
          this.testResult.text = 'plugged in';
          this.testStatus = 'OK';
          subscriber.next(new TestResponse(TestResponseCommands.NEXT));
        }
      }
    });
  }

  override runStep(): Observable<TestResponse> {
    if (!this._isPreparedForTest) {
      return of(new TestResponse(TestResponseCommands.GUEST, this.testQuestion));
    } else {
      return this.doRunStep().pipe(delay(500));
    }
  };
}

export class AudioPlayback extends Step {
  name = 'Audio playback';
  title = 'Checking audio playback...';

  constructor() {
    super(true);
    this.testQuestion = new TestQuestion('We playing a sound now. Can you hear it?', [{
      text: 'No',
      value: 'no',
      color: 'blue',
      isBlock: false,
      isDisabled:false
    },
      {text: 'Yes, I can', value: 'yes', color: 'blue', isBlock: false,isDisabled:false}],);
  }


  doRunStep(): any {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        this.testResult.errorMassage = 'You will not be able to hear the teacher or the other students. ' +
          'For suggestions on how to fix this, consult the classroom guide or ask tech support, ' +
          'then retry this step. If you have done everything currently possible, skip this step.';
        this.testResult.text = 'can\'t hear';
        this.testStatus = 'NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      }
      if (this.stepPayload === 'yes') {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = 'sound is okay';
        this.testStatus = 'OK';
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
    });
  };

}

export class AudioSource extends Step {
  name = 'Audio source';
  title = 'Checking audio source...';

  constructor() {
    super(true);
    this.testQuestion = new TestQuestion('Take off the headset. ' +
      'Does the sound come from headset or computer speakers?', [{
      text: 'Speakers',
      value: 'no',
      color: 'blue',
      isBlock: false,
      isDisabled:false
    },
      {
        text: 'Headset',
        value: 'yes'
        , color: 'blue',
        isBlock: false,
        isDisabled:false
      }]);
  }


  doRunStep() {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        this.testResult.errorMassage = 'Sound going through the speakers may be caught by your microphone and create an echo or a feedback loop.' +
          ' For suggestions on how to fix this, consult the classroom guide or ask tech support, then retry this step. If you have done everything ' +
          'currently possible, skip this step.';
        this.testResult.text = 'external speakers';
        this.testStatus = 'NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = 'headset speakers';
        this.testStatus = 'OK';
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
    });
  }
}
